import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { defaultSelectOption, EntityStatus } from '@core';
import { IncidentSeverity } from '@models';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '@ngrx-store';
import { BehaviorSubject, Observable } from 'rxjs';

import type { LocationData } from '../models';
import { LocationSelectors } from '../ngrx-store';

type BuildingStatusFilter = 'current' | 'archived';

export const siteStatusOptions = [
  defaultSelectOption,
  {
    title: 'Active',
    value: EntityStatus.Active,
  },
  {
    title: 'Paused',
    value: EntityStatus.Paused,
  },
];

@Injectable({
  providedIn: 'root',
})
export class BuildingDataFilterService {
  buildingStatusFilter$: Observable<BuildingStatusFilter>;
  filteredLocations$: Observable<LocationData[]>;
  siteStatusOptions = siteStatusOptions;
  _statusFilter = '';

  incidentOptions = [
    { checked: false, title: 'Critical', value: IncidentSeverity.CRITICAL },
    { checked: false, title: 'Major', value: IncidentSeverity.MAJOR },
    { checked: false, title: 'Minor', value: IncidentSeverity.MINOR },
  ];

  get showTabs() {
    return this._showTabs;
  }

  get buildingStatusFilter() {
    return this._buildingStatusFilter.value;
  }

  set buildingStatusFilter(value: BuildingStatusFilter) {
    this._buildingStatusFilter.next(value);
    this.filterLocations();
  }

  get search() {
    return this._search;
  }

  set search(value: string) {
    this._search = value;
    this.filterLocations();
  }

  get statusFilter() {
    return this._statusFilter;
  }

  set statusFilter(value: string) {
    this._statusFilter = value;
    this.filterLocations();
  }

  get incidentFilter() {
    return this.incidentOptions.filter(({ checked }) => checked).map(({ value }) => value);
  }

  set incidentFilter(filter: string[]) {
    this.incidentOptions = this.incidentOptions.map(option => {
      option.checked = filter.includes(option.value);

      return option;
    });
    this.filterLocations();
  }

  private _showTabs = true;
  private _buildingStatusFilter = new BehaviorSubject<BuildingStatusFilter>('current');
  private _locations: LocationData[] = [];
  private _filteredLocations: BehaviorSubject<LocationData[]> = new BehaviorSubject<LocationData[]>([]);
  private _search = '';
  private store = inject(Store);

  constructor() {
    this.buildingStatusFilter$ = this._buildingStatusFilter.asObservable();
    this.filteredLocations$ = this._filteredLocations.asObservable();

    this.store
      .select(AuthSelectors.isSuperAdmin)
      .pipe(takeUntilDestroyed())
      .subscribe(isSuperAdmin => (this._showTabs = isSuperAdmin));

    this.store
      .select(LocationSelectors.selectAllLocations)
      .pipe(takeUntilDestroyed())
      .subscribe(locations => {
        this.initLocations(locations);
      });
  }

  initLocations(locations: LocationData[]) {
    this._locations = locations;
    this.filterLocations();
  }

  updateIncidentFilter(filter: { checked: boolean; value: string; }) {
    this.incidentOptions = this.incidentOptions.map(option => {
      if (option.value === filter.value) {
        option.checked = filter.checked;
      }

      return option;
    });
    this.filterLocations();
  }

  clearFilters() {
    this.buildingStatusFilter = 'current';
    this.search = '';
    this.statusFilter = '';
    this.incidentFilter = [];
  }

  resetState() {
    this.clearFilters();
    this.initLocations([]);
  }

  private filterLocations() {
    const filteredLocations = this._locations.filter(location => {
      const statusMatch =
        this.buildingStatusFilter === 'archived'
          ? location.status === EntityStatus.Archived
          : this.statusFilter
            ? (location.status as string) === this.statusFilter
            : location.status !== EntityStatus.Archived;

      const friendlyNameMatch = !this.search || location.friendlyName.toLowerCase().includes(this.search.toLowerCase());

      const incidentMatch =
        !this.incidentFilter.length ||
        this.incidentFilter?.some(incidentType => {
          if (!location.incidentCountBySeverity) {
            return true;
          }

          if (incidentType === IncidentSeverity.CRITICAL) {
            return location.incidentCountBySeverity.critical > 0;
          }
          if (incidentType === IncidentSeverity.MAJOR) {
            return location.incidentCountBySeverity.major > 0;
          }
          if (incidentType === IncidentSeverity.MINOR) {
            return location.incidentCountBySeverity.minor > 0;
          }

          return false;
        });

      return statusMatch && friendlyNameMatch && incidentMatch;
    });

    this._filteredLocations.next(filteredLocations);
  }
}
